import { Heart, Coins, Lock } from "lucide-react";
import Constellation from "@/components/Constellation";

function Teaser({ icon: Icon, label, text, testId, onUnlock, full }) {
  if (full) {
    return (
      <div data-testid={testId} className="bg-[#0F1523] border border-[#D4AF37]/20 rounded-2xl p-5">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] gold-text mb-2"><Icon size={14} /> {label}</p>
        <p className="text-base leading-relaxed text-[#D6D0C5]">{full}</p>
      </div>
    );
  }
  return (
    <div
      data-testid={testId}
      onClick={onUnlock}
      className={`relative bg-[#0F1523] border border-[#D4AF37]/20 rounded-2xl p-5 ${onUnlock ? "cursor-pointer hover:border-[#D4AF37]/50 transition-colors" : ""}`}
    >
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] gold-text mb-2">
        <Icon size={14} /> {label} <Lock size={12} className="ml-auto text-[#A7B0C0]" />
      </p>
      <p className="text-base leading-relaxed text-[#D6D0C5]">{text}</p>
      <p className="text-base leading-relaxed text-[#D6D0C5] opacity-30 blur-[3px] select-none" aria-hidden>
        The stars hold more for you here, waiting to be read in full by those who look closer.
      </p>
      <p data-testid={`${testId}-unlock`} className="flex items-center gap-1.5 text-sm gold-text mt-2">
        <Lock size={13} /> Unlock with Premium
      </p>
    </div>
  );
}

// Public daily sample: full overall, love & money first line then blurred.
const PERIOD_LABEL = { week: "This Week", month: "This Month" };

export default function SignSample({ r, prefix, children, onUnlock, afterOverall, title, focus }) {
  const love = <Teaser key="love" icon={Heart} label="Love" text={r.love_teaser} full={r.love} testId={`${prefix}-love`} onUnlock={onUnlock} />;
  const money = <Teaser key="money" icon={Coins} label="Money" text={r.money_teaser} full={r.money} testId={`${prefix}-money`} onUnlock={onUnlock} />;
  const first = focus === "love" ? love : focus === "money" ? money : null;
  return (
    <div className="fade-up">
      <div className="flex flex-col items-center text-center mb-4">
        <Constellation sign={r.sign} size={110} />
        <p className="text-[11px] uppercase tracking-[0.3em] gold-text mt-1">
          {r.period === "month"
            ? new Date(r.date + "T12:00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" })
            : r.period === "week"
            ? `Week of ${new Date(r.date + "T12:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric" })}`
            : new Date(r.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        </p>
        <h1 data-testid={`${prefix}-title`} className="text-3xl font-light mt-1">{title || `${r.sign} ${PERIOD_LABEL[r.period] || "Today"}`}</h1>
        <p className="text-sm text-[#A7B0C0] mt-2">
          Mood · <span className="text-[#E8DCC4]">{r.mood}</span> · Lucky · <span className="text-[#E8DCC4]">{r.lucky_color}</span>
        </p>
      </div>
      <div className="space-y-3">
        {first}
        <div data-testid={`${prefix}-overall`} className="bg-[#0F1523] border border-[#D4AF37]/20 rounded-2xl p-5">
          <p className="text-[11px] uppercase tracking-[0.25em] gold-text mb-2">{PERIOD_LABEL[r.period] || "Today"}</p>
          <p className="text-base leading-relaxed text-[#D6D0C5]">{r.overall}</p>
        </div>
        {afterOverall}
        {focus !== "love" && love}
        {focus !== "money" && money}
        {children}
      </div>
    </div>
  );
}
